import { Box, BoxProps, styled, Typography } from '@mui/material';
import { alpha, css } from '@mui/system';
import { CSSProperties } from 'react';
import {
  SIGNAL_HEIGHT_L_PX,
  SIGNAL_HEIGHT_XL_PX,
  SIGNAL_ICON_ROW_PX,
  SIGNAL_THREE_ICON_ONE_ROW_MAX_PX
} from './CalendarAppointmentSignalLayout.utils';
import { getAppointmentOutlineStyle } from './CalenderAppointment.utils';
import type { AppointmentInteractionData } from './CalendarAppointment.types';
import {
  CalendarSlotType,
  getCalendarColorScheme
} from '../../Calendar.colors';
import {
  CHARCOAL,
  NEW_PRIMARY_LIGHT,
  NEW_RED_LIGHT,
  ON_SURFACE_VARIANT,
  RED_DARK,
  SLATE
} from 'constants/colors';

const explodedPlaneContentClamp = (lineClamp: number) =>
  css({
    '.explodedLayout': {
      '.planeContent': {
        WebkitLineClamp: lineClamp
      }
    }
  });

// Reusable signal icon display patterns
const signalCount2AllVisible = {
  '& .iconsLayout .signal-icon-column[data-signal-count="2"]': {
    '& > .signal-icon': { display: 'flex' },
    '& > .overflow-badge': { display: 'none !important' }
  }
};

const signalCount3AllVisible = {
  '& .iconsLayout .signal-icon-column[data-signal-count="3"]': {
    '& > .signal-icon': { display: 'flex !important' },
    '& > .overflow-badge': { display: 'none !important' }
  }
};

const signalCount4AllVisible = {
  '& .iconsLayout .signal-icon-column[data-signal-count="4"]': {
    '& > .signal-icon': { display: 'flex !important' },
    '& > .overflow-badge': { display: 'none !important' }
  }
};

const signalCount5AllVisible = {
  '& .iconsLayout .signal-icon-column[data-signal-count="5"]': {
    '& > .signal-icon': { display: 'flex !important' },
    '& > .overflow-badge': { display: 'none !important' }
  }
};

const signalCount4TwoIconsPlusBadge = {
  '& .iconsLayout .signal-icon-column[data-signal-count="4"]': {
    '& > .signal-icon:nth-of-type(1), & > .signal-icon:nth-of-type(2)': {
      display: 'flex'
    },
    '& > .overflow-badge.badge--plus2': { display: 'flex' }
  }
};

const signalCount5TwoIconsPlusBadge = {
  '& .iconsLayout .signal-icon-column[data-signal-count="5"]': {
    '& > .signal-icon:nth-of-type(1), & > .signal-icon:nth-of-type(2)': {
      display: 'flex'
    },
    '& > .overflow-badge.badge--plus3': { display: 'flex' }
  }
};

const signalCount5ThreeIconsPlusBadge = {
  '& .iconsLayout .signal-icon-column[data-signal-count="5"]': {
    '& > .signal-icon:nth-of-type(1), & > .signal-icon:nth-of-type(2), & > .signal-icon:nth-of-type(3)':
      {
        display: 'flex'
      },
    '& > .overflow-badge.badge--plus2': { display: 'flex' }
  }
};

export const CalendarAppointmentBorder = styled('div', {
  shouldForwardProp: (prop: string) =>
    !['interactionData', 'isNavigationSelected'].includes(prop)
})<{
  isNavigationSelected?: boolean;
  interactionData?: AppointmentInteractionData;
}>(({ theme, isNavigationSelected, interactionData }) => {
  const outlineStyle = getAppointmentOutlineStyle(
    !!isNavigationSelected,
    interactionData
  );

  return {
    position: 'relative',
    display: 'flex',
    height: '100%',
    borderRadius: theme.spacing(1),
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.vars.palette.surface.containerHigh,
    zIndex: 1,
    ...(outlineStyle && {
      '&:before': {
        content: '""',
        position: 'absolute',
        top: '-1px',
        bottom: '-1px',
        left: '-1px',
        right: '-1px',
        zIndex: 1,
        borderRadius: theme.spacing(1),
        outlineWidth: '3px',
        outlineOffset: '-3px',
        outlineColor: theme.vars.palette.outline.black,
        outlineStyle
      }
    })
  };
});

export const CalendarAppointmentContainer = styled(Box, {
  shouldForwardProp: (prop: string) =>
    ![
      'hasConflict',
      'hasTimeSlotBreach',
      'isRecurring',
      'interactionData',
      'isReadOnly'
    ].includes(prop)
})<
  BoxProps & {
    hasConflict?: boolean;
    hasTimeSlotBreach?: boolean;
    isRecurring?: boolean;
    interactionData?: AppointmentInteractionData;
    isReadOnly?: boolean;
  }
>(
  ({
    hasConflict,
    hasTimeSlotBreach,
    interactionData,
    isRecurring,
    isReadOnly,
    theme
  }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
    padding: theme.spacing(1, 3),
    boxSizing: 'border-box',
    backgroundColor: hasConflict ? NEW_RED_LIGHT : NEW_PRIMARY_LIGHT,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    position: 'relative',
    width: 'inherit',

    ...(interactionData?.isCut && interactionData?.isInteracted
      ? {
          textDecoration: 'line-through'
        }
      : {}),

    ...(isReadOnly && {
      color: theme.palette.grey[700],
      fontStyle: 'italic',
      ...(!hasConflict && { backgroundColor: theme.palette.grey[50] })
    }),

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: isRecurring ? '5px' : '2px',
      backgroundColor: hasConflict ? RED_DARK : SLATE,
      pointerEvents: 'none'
    },

    '& .explodedLayout': {
      display: 'none',
      position: 'absolute',
      overflow: 'hidden',
      height: '100%',
      top: 0,
      left: 0,
      right: 0,
      paddingBlock: theme.spacing(1.5, 3),
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(3.5)
    },

    // Hide icon with narrow calendars
    [`@container (width < ${theme.custom.ReactBigCalendar.eventsContainer.breakpoints.compact}px)`]:
      {
        '> div': {
          display: 'flex'
        }
      },

    '& .normalLayout': {
      display: 'block',
      width: 'inherit'
    },

    // Fill the card so @container inner-content height matches the slot, not only in-flow text
    '& .mainContentContainer': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      alignSelf: 'stretch',
      height: '100%',
      width: '100%',
      minHeight: 0,
      containerType: 'size',
      containerName: 'inner-content'
    },

    '@container card scroll-state((scrollable: y))': {
      '.normalLayout': {
        opacity: 0,
        pointerEvents: 'none',
        userSelect: 'none'
      },

      '.explodedLayout': {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,

        '& .planeContent': {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
          lineHeight: '16px'
        }
      }
    },

    '@container inner-content (height < 24px)': {
      '.explodedLayout': {
        '.planeContent': {
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden'
        }
      }
    },

    '@container inner-content (height > 24px) and (height < 45px)':
      explodedPlaneContentClamp(2),
    '@container inner-content (height > 60px)': explodedPlaneContentClamp(4),
    '@container inner-content (height > 70px)': explodedPlaneContentClamp(5),
    '@container inner-content (height > 76px)': explodedPlaneContentClamp(6),

    '@container inner-content (height > 85px)': {
      '.normalLayout': {
        opacity: 1,
        pointerEvents: 'unset',
        userSelect: 'unset'
      },

      '.explodedLayout': {
        display: 'none'
      }
    },

    '@container inner-content (width < 90px)': {
      '.normalLayout': {
        opacity: 0,
        pointerEvents: 'none',
        userSelect: 'none'
      },

      '.explodedLayout': {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 4,

        '& .planeContent': {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 4,
          overflow: 'hidden',
          lineHeight: '16px'
        }
      }
    },

    // Signal icons: @container inner-content + data-signal-count (parity with getVisibleSignalIconCount)
    // Icons are direct children of .signal-icon-column before badge spans — use > for reliable matching
    '& .iconsLayout': {
      '.signal-icon-column > .signal-icon': {
        display: 'none'
      },
      '.signal-icon-column > .overflow-badge': {
        display: 'none'
      }
    },

    '& .iconsLayout .signal-icon-column[data-signal-count="1"]': {
      '& > .signal-icon': {
        display: 'flex'
      },
      '& > .overflow-badge': {
        display: 'none !important'
      }
    },

    [`@container inner-content (height < ${SIGNAL_ICON_ROW_PX}px)`]: {
      '& .iconsLayout .signal-icon-column[data-signal-count="2"]': {
        '& > .signal-icon': { display: 'none' },
        '& > .overflow-badge.badge--all': { display: 'flex' }
      },
      '& .iconsLayout .signal-icon-column[data-signal-count="3"]': {
        '& > .signal-icon': { display: 'none' },
        '& > .overflow-badge.badge--all': { display: 'flex' }
      },
      '& .iconsLayout .signal-icon-column[data-signal-count="4"]': {
        '& > .signal-icon': { display: 'none' },
        '& > .overflow-badge.badge--all': { display: 'flex' }
      },
      '& .iconsLayout .signal-icon-column[data-signal-count="5"]': {
        '& > .signal-icon': { display: 'none' },
        '& > .overflow-badge.badge--all': { display: 'flex' }
      }
    },

    [`@container inner-content (height >= ${SIGNAL_ICON_ROW_PX}px) and (height <= ${SIGNAL_THREE_ICON_ONE_ROW_MAX_PX}px)`]:
      {
        '& .iconsLayout .signal-icon-column[data-signal-count="2"]': {
          '& > .signal-icon': { display: 'flex' },
          '& > .overflow-badge': { display: 'none !important' }
        },
        '& .iconsLayout .signal-icon-column[data-signal-count="3"]': {
          '& > .signal-icon:nth-of-type(1)': { display: 'flex' },
          '& > .overflow-badge.badge--plus2': { display: 'flex' }
        },
        '& .iconsLayout .signal-icon-column[data-signal-count="4"]': {
          '& > .signal-icon:nth-of-type(1)': { display: 'flex' },
          '& > .overflow-badge.badge--plus3': { display: 'flex' }
        },
        '& .iconsLayout .signal-icon-column[data-signal-count="5"]': {
          '& > .signal-icon:nth-of-type(1)': { display: 'flex' },
          '& > .overflow-badge.badge--plus4': { display: 'flex' }
        }
      },

    [`@container inner-content (height > ${SIGNAL_THREE_ICON_ONE_ROW_MAX_PX}px) and (height < ${SIGNAL_HEIGHT_L_PX}px)`]:
      {
        ...signalCount2AllVisible,
        ...signalCount3AllVisible,
        ...signalCount4TwoIconsPlusBadge,
        ...signalCount5TwoIconsPlusBadge
      },

    [`@container inner-content (height >= ${SIGNAL_HEIGHT_L_PX}px) and (height < ${SIGNAL_HEIGHT_XL_PX}px)`]:
      {
        ...signalCount2AllVisible,
        ...signalCount3AllVisible,
        ...signalCount4AllVisible,
        ...signalCount5ThreeIconsPlusBadge
      },

    [`@container inner-content (height >= ${SIGNAL_HEIGHT_XL_PX}px)`]: {
      ...signalCount2AllVisible,
      ...signalCount3AllVisible,
      ...signalCount4AllVisible,
      ...signalCount5AllVisible
    },

    ...(hasTimeSlotBreach && {
      opacity: 0.5
    })
  })
);

export const TextContainer = styled(Box)<{ lineClamp?: number }>(
  ({ lineClamp = 2 }) => ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lineClamp,
    margin: 0,
    lineHeight: 'inherit',
    hyphens: 'manual',
    whiteSpace: 'nowrap'
  })
);

export const IconContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !['iconColor'].includes(prop)
})<{ iconColor?: string }>(({ theme, iconColor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: 'inherit',
  height: '16px',

  svg: {
    width: '16px',
    height: '16px',
    color: iconColor
  }
}));

export const CustomIconContainer = styled(IconContainer)(() => ({
  height: '100%',
  alignItems: 'start'
}));

export const Title = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.body1.fontSize,
  fontFamily: 'SourceSansPro-Bold',
  color: CHARCOAL
}));

export const TimeLabel = styled('span')(({ theme }) => ({
  color: ON_SURFACE_VARIANT,
  fontSize: theme.typography.body2.fontSize
}));

export const PreviewAppointmentContainer = styled(
  CalendarAppointmentContainer,
  {
    shouldForwardProp: (prop) => prop != 'hasOverlap'
  }
)<{ hasOverlap?: boolean }>(({ theme, hasOverlap }) => ({
  display: 'flex',
  zIndex: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0px 0px 0px 1px ${theme.vars.shadows[10]}`,
  width: 'calc(100% - 1px)',

  backgroundColor: hasOverlap
    ? theme.vars.palette.warning.container
    : theme.vars.palette.success.container,
  border: `1px solid ${
    hasOverlap
      ? theme.vars.palette.warning.dark
      : theme.vars.palette.success.dark
  }`,
  svg: {
    width: '40px',
    height: '40px',
    color: hasOverlap
      ? theme.vars.palette.warning.dark
      : theme.vars.palette.success.dark
  },
  '&::before': {
    content: 'none'
  }
}));

export const OriginalPlaceholderAppointmentContainer = styled(
  CalendarAppointmentContainer
)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0px 0px 0px 1px ${theme.vars.shadows[10]}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.15),
  border: `1px dashed ${alpha(theme.palette.primary.dark, 0.5)}`
}));

export const AvailabilityBackgroundContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'type'
})<{ type: CalendarSlotType }>(({ theme, type }) => ({
  height: '100%',
  width: '100%',
  backgroundColor: getCalendarColorScheme(type, theme).washColor,
  pointerEvents: 'none',
  padding: '4px 8px',
  boxSizing: 'border-box',
  fontSize: '11px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  textTransform: 'uppercase'
}));

export const StyledEntityInfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  alignItems: 'start',
  columnGap: theme.spacing(2),
  width: '100%'
}));

export const StyledEntityTextTypography = styled(Typography)(() => ({
  width: 'fit-content',
  lineHeight: 'normal',
  color: ON_SURFACE_VARIANT
}));

export const StyledCalendarContainerBox = styled(Box)(() => ({
  height: 'inherit',
  width: 'inherit'
}));

export const StyledOverflowContainerBox = styled(Box)(() => ({
  width: '92%',

  '& *': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}));

const EaseUpDown: CSSProperties & {
  '@keyframes easeUpDown': Record<string, CSSProperties>;
} = {
  animation: 'easeUpDown 1s infinite',
  '@keyframes easeUpDown': {
    '0%': {
      transform: 'translateY(0)'
    },
    '50%': {
      transform: 'translateY(-2px)'
    },
    '100%': {
      transform: 'translateY(0)'
    }
  }
};
export const TimeSlotBreachIndicator = styled(Box)({
  position: 'absolute',
  top: '4px',
  left: 'calc(50% - 8px)',
  ...EaseUpDown,
  svg: {
    width: '16px',
    height: '16px'
  }
});
